import { useEffect } from "react";

const SITE_NAME = "JCMR Portafolio";
const SITE_URL = "https://portafoliojcmr.netlify.app";
const DEFAULT_IMAGE = `${SITE_URL}/imgs/foto.jpg`;
const DEFAULT_AUTHOR = "Juan Camilo Muñoz";
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

function normalizePath(path = "/") {
  if (!path || path === "/") {
    return "/";
  }

  return path.startsWith("/") ? path : `/${path}`;
}

function absoluteUrl(path = "/") {
  const normalizedPath = normalizePath(path);
  return `${SITE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`;
}

function upsertMeta(selector, attributeName, value, attributeValue) {
  if (value === undefined || value === null || value === "") {
    const existing = document.head.querySelector(selector);

    if (existing) {
      existing.remove();
    }

    return;
  }

  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  element.setAttribute(attributeName, attributeValue);
  element.setAttribute("content", value);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function buildStructuredData(data) {
  if (!data) {
    return null;
  }

  return Array.isArray(data) ? data : [data];
}

function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  keywords = [],
  noIndex = false,
  structuredData = null,
}) {
  useEffect(() => {
    const normalizedPath = normalizePath(path);
    const pageUrl = absoluteUrl(normalizedPath);
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const robotsContent = noIndex ? "noindex, nofollow" : DEFAULT_ROBOTS;

    document.title = fullTitle;
    document.documentElement.lang = "es";

    upsertMeta('meta[name="description"]', "name", description, "description");
    upsertMeta('meta[name="author"]', "name", DEFAULT_AUTHOR, "author");
    upsertMeta('meta[name="robots"]', "name", robotsContent, "robots");
    upsertMeta('meta[name="keywords"]', "name", keywords.length ? keywords.join(", ") : "", "keywords");
    upsertMeta('meta[property="og:title"]', "property", fullTitle, "og:title");
    upsertMeta('meta[property="og:description"]', "property", description, "og:description");
    upsertMeta('meta[property="og:type"]', "property", type, "og:type");
    upsertMeta('meta[property="og:url"]', "property", pageUrl, "og:url");
    upsertMeta('meta[property="og:image"]', "property", image, "og:image");
    upsertMeta('meta[property="og:image:alt"]', "property", `${SITE_NAME} - ${title || "Portafolio"}`, "og:image:alt");
    upsertMeta('meta[property="og:site_name"]', "property", SITE_NAME, "og:site_name");
    upsertMeta('meta[property="og:locale"]', "property", "es_CO", "og:locale");
    upsertMeta('meta[name="twitter:card"]', "name", "summary_large_image", "twitter:card");
    upsertMeta('meta[name="twitter:title"]', "name", fullTitle, "twitter:title");
    upsertMeta('meta[name="twitter:description"]', "name", description, "twitter:description");
    upsertMeta('meta[name="twitter:image"]', "name", image, "twitter:image");
    upsertMeta('meta[name="twitter:image:alt"]', "name", `${SITE_NAME} - ${title || "Portafolio"}`, "twitter:image:alt");
    upsertLink("canonical", pageUrl);

    let script = document.getElementById("seo-jsonld");
    const jsonLd = buildStructuredData(structuredData);

    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = "seo-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, path, image, type, keywords, noIndex, structuredData]);

  return null;
}

export default SEO;
